import React, { useState } from 'react'
import ArtistBubble from './ArtistBubble'
import DayAssigner from './DayAssigner'
import { dayIndexFromLineup, groupArtistsByDay } from '../../utils/scheduleUtils'

const ArtistSection = ({ event, user, onArtistToggle, onCommentClick, onAssignDay }) => {
  const isOwner = event.createdBy?._id?.toString() === user?.id?.toString()
  const isFutureEvent = event.endDate && new Date(event.endDate) > new Date()

  if (!event.artistsSeen || event.artistsSeen.length === 0) {
    return event.lineup ? <LineupFallback lineup={event.lineup} /> : null
  }

  return (
    <div className="space-y-6">
      <ArtistsList
        event={event}
        artists={event.artistsSeen}
        sharedDays={dayIndexFromLineup(event.officialLineup)}
        onAssignDay={onAssignDay}
        isOwner={isOwner}
        isFutureEvent={isFutureEvent}
        onToggle={onArtistToggle}
        onCommentClick={onCommentClick}
      />

      <ArtistComments
        artists={event.artistsSeen}
        isOwner={isOwner}
        onEditClick={onCommentClick}
      />

      <FavoriteArtists
        artists={event.artistsSeen}
        isOwner={isOwner}
        isFutureEvent={isFutureEvent}
        onToggle={onArtistToggle}
        onCommentClick={onCommentClick}
      />
    </div>
  )
}

const ArtistsList = ({ event, artists, sharedDays, isOwner, isFutureEvent, onToggle, onCommentClick, onAssignDay }) => {
  const [assigning, setAssigning] = useState(false)

  return (
  <div>
    <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
      <h3 className="text-lg font-semibold text-white">🎤 Artists</h3>
      {isOwner && onAssignDay && !assigning && (
        <button
          type="button"
          onClick={() => setAssigning(true)}
          className="text-xs bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-lg border border-white/20 transition-colors"
        >
          📅 Assign days
        </button>
      )}
    </div>

    {/* Artist Color Legend */}
    {isOwner && (
      <div className="mb-4 bg-white/5 backdrop-blur-xl rounded-lg border border-white/10 p-3">
        <div className="flex flex-wrap items-center gap-3 text-xs">
          <span className="font-medium text-gray-300">Legend:</span>
          {isFutureEvent ? (
            // Future event legend
            <div className="flex items-center gap-1.5">
              <div className="w-6 h-6 rounded-full border-2 border-purple-500/60 bg-purple-500/20"></div>
              <span className="text-purple-300">Want to see 👁</span>
            </div>
          ) : (
            // Past event legend
            <>
              <div className="flex items-center gap-1.5">
                <div className="w-6 h-6 rounded-full border-2 border-green-500/60 bg-green-500/20"></div>
                <span className="text-green-300">Saw them</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-6 h-6 rounded-full border-2 border-amber-500/60 bg-amber-500/20"></div>
                <span className="text-amber-300">Wanted to see</span>
              </div>
            </>
          )}
          <div className="flex items-center gap-1.5">
            <span className="text-yellow-400">⭐</span>
            <span className="text-yellow-200">Favorite</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-blue-300">💬</span>
            <span className="text-blue-200">Has comment</span>
          </div>
        </div>
      </div>
    )}

    {assigning ? (
      <DayAssigner
        event={event}
        artists={artists}
        sharedDays={sharedDays}
        onAssign={onAssignDay}
        onDone={() => setAssigning(false)}
      />
    ) : (
      <LineupBody
        artists={artists}
        sharedDays={sharedDays}
        isOwner={isOwner}
        isFutureEvent={isFutureEvent}
        onToggle={onToggle}
        onCommentClick={onCommentClick}
      />
    )}
  </div>
  )
}

/**
 * Splits the lineup into per-day blocks once the days are actually known,
 * mirroring how a festival flyer prints them: earliest day at the top.
 *
 * Day data only arrives through flyer extraction, so most events have none —
 * and a single-day event gains nothing from a header announcing its one day.
 * Both of those fall through to the original flat list, which is why this is a
 * progressive improvement rather than a redesign.
 */
const LineupBody = ({ artists, sharedDays, isOwner, isFutureEvent, onToggle, onCommentClick }) => {
  const { days, undated, dayCount } = groupArtistsByDay(artists, sharedDays)

  if (dayCount < 2) {
    return <ArtistRun entries={artists.map((artist, index) => ({ artist, index }))}
      isOwner={isOwner} isFutureEvent={isFutureEvent}
      onToggle={onToggle} onCommentClick={onCommentClick} />
  }

  return (
    <div className="space-y-5">
      {days.map(day => (
        <DayBlock
          key={day.dayKey}
          heading={`Day ${day.dayNumber}`}
          subheading={day.label}
          count={day.entries.length}
          entries={day.entries}
          isOwner={isOwner}
          isFutureEvent={isFutureEvent}
          onToggle={onToggle}
          onCommentClick={onCommentClick}
        />
      ))}

      {undated.length > 0 && (
        // Never hidden. A flyer that assigns most of the lineup to days and
        // leaves a few unplaced is common; dropping those artists would quietly
        // shorten the lineup.
        <DayBlock
          heading="Day not listed"
          subheading="No day on the flyer yet"
          count={undated.length}
          entries={undated}
          isOwner={isOwner}
          isFutureEvent={isFutureEvent}
          onToggle={onToggle}
          onCommentClick={onCommentClick}
        />
      )}
    </div>
  )
}

const DayBlock = ({ heading, subheading, count, entries, isOwner, isFutureEvent, onToggle, onCommentClick }) => (
  <section className="bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 p-4">
    <div className="flex items-baseline justify-between gap-3 mb-3 pb-2 border-b border-white/10">
      <div className="flex items-baseline gap-2 flex-wrap">
        <h4 className="text-white font-semibold">{heading}</h4>
        <span className="text-sm text-gray-400">{subheading}</span>
      </div>
      <span className="text-xs text-gray-400 flex-shrink-0">
        {count} {count === 1 ? 'artist' : 'artists'}
      </span>
    </div>

    <ArtistRun
      entries={entries}
      isOwner={isOwner}
      isFutureEvent={isFutureEvent}
      onToggle={onToggle}
      onCommentClick={onCommentClick}
    />
  </section>
)

/**
 * `entry.index` is the artist's position in event.artistsSeen, NOT its position
 * in this block. The toggle and comment handlers address artists by that index,
 * so passing the loop counter here would edit whichever artist happened to sit
 * at that spot in the day.
 */
const ArtistRun = ({ entries, isOwner, isFutureEvent, onToggle, onCommentClick }) => (
  <div className="text-gray-300 leading-relaxed">
    {entries.map((entry, position) => (
      <ArtistBubble
        key={entry.index}
        artist={entry.artist}
        index={entry.index}
        isOwner={isOwner}
        isFutureEvent={isFutureEvent}
        onToggle={onToggle}
        onCommentClick={onCommentClick}
        showSeparator={position < entries.length - 1}
      />
    ))}
  </div>
)

const ArtistComments = ({ artists, isOwner, onEditClick }) => {
  const artistsWithComments = artists.filter(artist => artist.comment && artist.comment.trim() !== '')

  if (artistsWithComments.length === 0) return null

  return (
    <div>
      <h3 className="text-lg font-semibold text-white mb-3">💬 Artist Comments</h3>
      <div className="space-y-3">
        {artistsWithComments.map((artist, index) => (
          <div key={index} className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <span className="text-blue-300 font-semibold">{artist.name}:</span>
              <span className="text-blue-200 italic flex-1">{artist.comment}</span>
              {isOwner && (
                <button
                  onClick={() => {
                    const actualIndex = artists.findIndex(a => a.name === artist.name)
                    onEditClick(actualIndex)
                  }}
                  className="text-blue-400 hover:text-blue-300 transition-colors flex-shrink-0"
                  title="Edit artist details"
                >
                  ✏️
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

const FavoriteArtists = ({ artists, isOwner, isFutureEvent, onToggle, onCommentClick }) => {
  const favoriteArtists = artists.filter(artist => artist.favorite)

  if (favoriteArtists.length === 0) return null

  return (
    <div>
      <h3 className="text-lg font-semibold text-white mb-3">⭐ Favorite Performances</h3>
      <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/30 rounded-xl p-4">
        <div className="text-yellow-200 leading-relaxed">
          {favoriteArtists.map((artist, favoriteIndex) => {
            const originalIndex = artists.findIndex(a => a.name === artist.name)
            return (
              <ArtistBubble
                key={favoriteIndex}
                artist={artist}
                index={originalIndex}
                isOwner={isOwner}
                isFutureEvent={isFutureEvent}
                isInFavorites={true}
                onToggle={onToggle}
                onCommentClick={onCommentClick}
                showSeparator={favoriteIndex < favoriteArtists.length - 1}
              />
            )
          })}
        </div>
      </div>
    </div>
  )
}

const LineupFallback = ({ lineup }) => (
  <div>
    <h3 className="text-lg font-semibold text-white mb-3">🎤 Lineup</h3>
    <p className="text-gray-300">{lineup.replace(/,\s*/g, ' ')}</p>
  </div>
)

export default ArtistSection